import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import { useConsoleStore } from '../store';

type PromoCode = {
  id: string;
  code: string;
  discountType: 'percent' | 'fixed' | 'free_forever';
  discountValue: number;
  applicableTiers: string[];
  maxRedemptions: number | null;
  redemptionsUsed: number;
  expiresAt: string | null;
  isActive: boolean;
  createdBy: string | null;
  notes: string | null;
  createdAt: string;
};

type StoreRow = {
  id: string;
  externalDomain: string;
  platform: string;
  isActive: boolean;
  planTier: string;
  subscriptionStatus: string;
  billingProvider: string;
  compedReason?: string | null;
  createdAt: string;
};

const TIERS = ['free', 'growth', 'agent', 'rep', 'vea'] as const;

function formatDate(v?: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function platformBadge(platform?: string) {
  const p = (platform || '').toLowerCase();
  if (p === 'shopify') {
    return <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Shopify</span>;
  }
  if (p === 'wordpress') {
    return <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">WordPress</span>;
  }
  return <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-semibold text-zinc-700">{platform || 'Unknown'}</span>;
}

export default function PromoCodes() {
  const { user } = useConsoleStore();
  const role = (user?.role || '').toLowerCase();
  const canStaff = role === 'staff' || role === 'superadmin';

  const [code, setCode] = useState('');
  const [discountType, setDiscountType] = useState<'percent' | 'fixed' | 'free_forever'>('percent');
  const [discountValue, setDiscountValue] = useState(0);
  const [selectedTiers, setSelectedTiers] = useState<string[]>([]);
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [notes, setNotes] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [toggleLoadingById, setToggleLoadingById] = useState<Record<string, boolean>>({});

  const promoQuery = useQuery({
    queryKey: ['internal-promo-codes'],
    queryFn: async () => {
      const res = await apiFetch<{ codes: PromoCode[] }>('/api/internal/promo-codes');
      return res.codes || [];
    },
  });

  const storesQuery = useQuery({
    queryKey: ['internal-stores'],
    queryFn: async () => {
      const data = await apiFetch<any>('/api/internal/stores');
      if (Array.isArray(data)) return data as StoreRow[];
      if (Array.isArray(data?.stores)) return data.stores as StoreRow[];
      return [] as StoreRow[];
    },
  });

  const compedStores = useMemo(
    () => (storesQuery.data || []).filter((s) => (s.subscriptionStatus || '').toLowerCase() === 'comped'),
    [storesQuery.data]
  );

  const toggleTier = (tier: string) => {
    setSelectedTiers((prev) => (prev.includes(tier) ? prev.filter((t) => t !== tier) : [...prev, tier]));
  };

  const onCreate = async () => {
    if (!canStaff) return;
    setCreateLoading(true);
    setCreateError(null);
    setCreateSuccess(null);
    try {
      await apiFetch('/api/internal/promo-codes', {
        method: 'POST',
        body: JSON.stringify({
          code: code.trim().toUpperCase(),
          discountType,
          discountValue: discountType === 'free_forever' ? 0 : discountValue,
          applicableTiers: selectedTiers,
          maxRedemptions: maxRedemptions.trim() ? Number(maxRedemptions) : null,
          expiresAt: expiresAt ? new Date(`${expiresAt}T00:00:00.000Z`).toISOString() : null,
          notes: notes.trim() || undefined,
        }),
      });
      setCreateSuccess('Promo code created.');
      setCode('');
      setDiscountType('percent');
      setDiscountValue(0);
      setSelectedTiers([]);
      setMaxRedemptions('');
      setExpiresAt('');
      setNotes('');
      promoQuery.refetch();
    } catch (err: any) {
      setCreateError(err?.message || 'Failed to create promo code');
    } finally {
      setCreateLoading(false);
    }
  };

  const onToggleActive = async (row: PromoCode) => {
    if (!canStaff) return;
    setToggleLoadingById((prev) => ({ ...prev, [row.id]: true }));
    try {
      await apiFetch(`/api/internal/promo-codes/${row.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !row.isActive }),
      });
      promoQuery.refetch();
    } finally {
      setToggleLoadingById((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  return (
    <div className="space-y-6 p-6">
      {canStaff && (
        <div className="rounded-2xl border border-zinc-200 bg-white p-6">
          <h2 className="mb-4 text-lg font-semibold text-zinc-900">Create Promo Code</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Code"
              className="rounded-xl border border-zinc-300 px-3 py-2 text-sm"
            />
            <select
              value={discountType}
              onChange={(e) => setDiscountType(e.target.value as any)}
              className="rounded-xl border border-zinc-300 px-3 py-2 text-sm"
            >
              <option value="percent">percent</option>
              <option value="fixed">fixed</option>
              <option value="free_forever">free_forever</option>
            </select>

            {discountType !== 'free_forever' && (
              <input
                type="number"
                value={discountValue}
                onChange={(e) => setDiscountValue(Number(e.target.value || 0))}
                placeholder={discountType === 'percent' ? 'Discount percent' : 'Discount cents'}
                className="rounded-xl border border-zinc-300 px-3 py-2 text-sm"
              />
            )}

            <input
              type="number"
              value={maxRedemptions}
              onChange={(e) => setMaxRedemptions(e.target.value)}
              placeholder="Max redemptions (blank = unlimited)"
              className="rounded-xl border border-zinc-300 px-3 py-2 text-sm"
            />
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="rounded-xl border border-zinc-300 px-3 py-2 text-sm"
            />
          </div>

          <div className="mt-4">
            <p className="mb-2 text-sm font-medium text-zinc-700">Applicable tiers (empty = all)</p>
            <div className="flex flex-wrap gap-3">
              {TIERS.map((tier) => (
                <label key={tier} className="inline-flex items-center gap-2 text-sm text-zinc-700">
                  <input
                    type="checkbox"
                    checked={selectedTiers.includes(tier)}
                    onChange={() => toggleTier(tier)}
                  />
                  <span className="capitalize">{tier}</span>
                </label>
              ))}
            </div>
          </div>

          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)"
            className="mt-4 min-h-[90px] w-full rounded-xl border border-zinc-300 px-3 py-2 text-sm"
          />

          <button
            onClick={onCreate}
            disabled={createLoading || !code.trim()}
            className="mt-4 rounded-xl bg-zinc-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {createLoading ? 'Creating...' : 'Create Promo Code'}
          </button>
          {createError && <p className="mt-2 text-sm text-red-600">{createError}</p>}
          {createSuccess && <p className="mt-2 text-sm text-emerald-600">{createSuccess}</p>}
        </div>
      )}

      <div className="rounded-2xl border border-zinc-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-zinc-900">Promo Codes</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="text-zinc-500">
              <tr className="border-b border-zinc-200">
                <th className="px-3 py-2 font-medium">Code</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Value</th>
                <th className="px-3 py-2 font-medium">Tiers</th>
                <th className="px-3 py-2 font-medium">Used/Max</th>
                <th className="px-3 py-2 font-medium">Expires</th>
                <th className="px-3 py-2 font-medium">Active</th>
                <th className="px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(promoQuery.data || []).map((row) => (
                <tr key={row.id} className="border-b border-zinc-100">
                  <td className="px-3 py-2 font-medium text-zinc-900">{row.code}</td>
                  <td className="px-3 py-2">{row.discountType}</td>
                  <td className="px-3 py-2">
                    {row.discountType === 'percent'
                      ? `${row.discountValue}%`
                      : row.discountType === 'fixed'
                        ? `$${(row.discountValue / 100).toFixed(2)}`
                        : '—'}
                  </td>
                  <td className="px-3 py-2">{Array.isArray(row.applicableTiers) && row.applicableTiers.length ? row.applicableTiers.join(', ') : 'all'}</td>
                  <td className="px-3 py-2">{row.redemptionsUsed}/{row.maxRedemptions ?? '∞'}</td>
                  <td className="px-3 py-2">{formatDate(row.expiresAt)}</td>
                  <td className="px-3 py-2">{row.isActive ? 'Yes' : 'No'}</td>
                  <td className="px-3 py-2">
                    {canStaff ? (
                      <button
                        onClick={() => onToggleActive(row)}
                        disabled={!!toggleLoadingById[row.id]}
                        className="rounded-lg border border-zinc-300 px-2 py-1 text-xs font-medium text-zinc-700"
                      >
                        {row.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-zinc-900">Comped Stores</h2>
        {compedStores.length === 0 ? (
          <p className="text-sm text-zinc-500">No comped stores yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-zinc-500">
                <tr className="border-b border-zinc-200">
                  <th className="px-3 py-2 font-medium">Domain</th>
                  <th className="px-3 py-2 font-medium">Platform</th>
                  <th className="px-3 py-2 font-medium">Comped Reason</th>
                </tr>
              </thead>
              <tbody>
                {compedStores.map((s) => (
                  <tr key={s.id} className="border-b border-zinc-100">
                    <td className="px-3 py-2 text-zinc-900">{s.externalDomain}</td>
                    <td className="px-3 py-2">{platformBadge(s.platform)}</td>
                    <td className="px-3 py-2 text-zinc-700">{s.compedReason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
